import { getSearchPath } from "src/consts/urlPaths";

export const ATTRIBUTE_FIELD_PARAM = "attribute_field";
export const ATTRIBUTE_VALUE_PARAM = "attribute_value";
export const ATTRIBUTE_TEAM_PARAM = "attribute_team";
export const TAG_PARAM = "tag";
export const TAG_LABEL_PARAM = "tag_label";
export const SUPPLY_TEAM_PARAM = "supply_team";

export type AttributePeopleFilter = {
  field: string;
  value: string;
};

export type TagPeopleFilter = {
  kind: "tag";
  tagId: string;
  label: string;
};

export type ChartPeopleFilter =
  | { kind?: "attribute"; field: string; value: string }
  | TagPeopleFilter;

export type AttributePeopleSearch = {
  teamId: string;
  filters: Array<AttributePeopleFilter | TagPeopleFilter>;
  supplyTeamId?: string;
};

const isTagFilter = (
  filter: ChartPeopleFilter | AttributePeopleFilter
): filter is TagPeopleFilter => "kind" in filter && filter.kind === "tag";

/** People page for this team, filtered to the chart value. */
export const workerAttributePeoplePath = (
  teamId: string,
  filters: ChartPeopleFilter[],
  supplyTeamId?: string
): string => {
  const path = getSearchPath("people", null, false);
  const params = new URLSearchParams();
  if (teamId) {
    params.set(ATTRIBUTE_TEAM_PARAM, teamId);
  }
  const supplyTeam = supplyTeamId?.trim() ?? "";
  if (supplyTeam) {
    params.set(SUPPLY_TEAM_PARAM, supplyTeam);
  }
  for (const filter of filters) {
    if (isTagFilter(filter)) {
      const tagId = filter.tagId.trim();
      if (!tagId || tagId === "Not set") {
        continue;
      }
      params.append(TAG_PARAM, tagId);
      params.append(TAG_LABEL_PARAM, filter.label.trim() || tagId);
      continue;
    }
    const field = filter.field.trim();
    const value = filter.value.trim();
    if (!field || value === "Not set") {
      continue;
    }
    params.append(ATTRIBUTE_FIELD_PARAM, field);
    params.append(ATTRIBUTE_VALUE_PARAM, value);
  }
  const search = params.toString();
  return search ? `${path}?${search}` : path;
};

export const parseWorkerAttributePeopleSearch = (
  search: string
): AttributePeopleSearch | null => {
  const params = new URLSearchParams(search);
  const fields = params.getAll(ATTRIBUTE_FIELD_PARAM);
  const values = params.getAll(ATTRIBUTE_VALUE_PARAM);
  const attributeFilters = fields.flatMap((field, index) => {
    const trimmedField = field.trim();
    const value = (values[index] ?? "").trim();
    if (!trimmedField || value === "Not set") {
      return [];
    }
    return [{ field: trimmedField, value }];
  });
  const tagIds = params.getAll(TAG_PARAM);
  const tagLabels = params.getAll(TAG_LABEL_PARAM);
  const tagFilters = tagIds.flatMap((tagId, index) => {
    const id = tagId.trim();
    if (!id) {
      return [];
    }
    return [
      {
        kind: "tag" as const,
        tagId: id,
        label: (tagLabels[index] ?? id).trim() || id,
      },
    ];
  });
  const filters = [...attributeFilters, ...tagFilters];
  const teamId = params.get(ATTRIBUTE_TEAM_PARAM)?.trim() ?? "";
  const supplyTeamId = params.get(SUPPLY_TEAM_PARAM)?.trim() ?? "";
  if (filters.length === 0 && !teamId && !supplyTeamId) {
    return null;
  }
  return {
    teamId,
    filters,
    ...(supplyTeamId ? { supplyTeamId } : {}),
  };
};

/** Query string to keep when other search filters are stripped. */
export const workerAttributePeopleSearch = (search: string): string => {
  const parsed = parseWorkerAttributePeopleSearch(search);
  if (!parsed) {
    return "";
  }
  const params = new URLSearchParams();
  if (parsed.teamId) {
    params.set(ATTRIBUTE_TEAM_PARAM, parsed.teamId);
  }
  if (parsed.supplyTeamId) {
    params.set(SUPPLY_TEAM_PARAM, parsed.supplyTeamId);
  }
  for (const filter of parsed.filters) {
    if (isTagFilter(filter)) {
      params.append(TAG_PARAM, filter.tagId);
      params.append(TAG_LABEL_PARAM, filter.label);
      continue;
    }
    params.append(ATTRIBUTE_FIELD_PARAM, filter.field);
    params.append(ATTRIBUTE_VALUE_PARAM, filter.value);
  }
  return `?${params.toString()}`;
};

/** Chart links for country and company use the person filters, not a bag attribute. */
export const insightFilterFromAttributePeople = (
  parsed: AttributePeopleSearch
): {
  personAttributes?: Array<{ field: string; values: string[] }>;
  country?: string[];
  company?: string[];
  personTags?: string[];
  teams?: {
    teams: string[];
    includeIndirectTeamMembers: true;
    includeSubTeams: true;
    directTeams?: string[];
  };
} => {
  const country: string[] = [];
  const company: string[] = [];
  const bags: AttributePeopleFilter[] = [];
  for (const filter of parsed.filters) {
    if (isTagFilter(filter)) {
      continue;
    }
    if (!filter.value) {
      continue;
    }
    if (filter.field === "country") {
      country.push(filter.value);
    } else if (filter.field === "company") {
      company.push(filter.value);
    } else {
      bags.push(filter);
    }
  }
  const tags = parsed.filters.filter(isTagFilter);
  return {
    ...(bags.length > 0
      ? {
          personAttributes: bags.map((item) => ({
            field: item.field,
            values: [item.value],
          })),
        }
      : {}),
    ...(country.length > 0 ? { country } : {}),
    ...(company.length > 0 ? { company } : {}),
    ...(tags.length > 0 ? { personTags: tags.map((item) => item.tagId) } : {}),
    ...(parsed.teamId
      ? {
          teams: {
            teams: [parsed.teamId],
            includeIndirectTeamMembers: true as const,
            includeSubTeams: true as const,
            ...(parsed.supplyTeamId
              ? { directTeams: [parsed.supplyTeamId] }
              : {}),
          },
        }
      : {}),
  };
};
